import React, { useState } from "react";
import styled from "styled-components";
import { Heading3 } from "app/popleads/components/ui-kit/Text";
import ExtensionInputGroup from "./ExtensionInputGroup";
import { post } from "app/popleads/networking/RequestService";
import { Formik, Form, Field } from "formik";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { colors } from "app/popleads/components/ui-kit/colors";
import InputWithIcon from "app/popleads/components/ui-kit/Input";
import SVG from "react-inlinesvg";
import ExtensionCard from "app/popleads/components/ui-kit/ExtensionCard";
import { toAbsoluteUrl } from "_metronic/_helpers";
import FeatherIcon from "feather-icons-react";
import { useExtensionContext } from "../ExtensionContext";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import useLocationOptions from "app/popleads/hooks/useLocationOptions";

const NewLeadContainer = styled.div`
    padding: 0px 12px;
    overflow-y: auto;
    height: calc(100% - 50px);
`;

const StyledForm = styled(Form)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
`;

const ImageSection = styled.div`
    min-height: 88px;
    min-width: 88px;
    max-height: 88px;
    max-width: 88px;
    border-radius: 50%;
    padding: 1px;
    opacity: 1;
    background: transparent
        linear-gradient(
            90deg,
            ${colors.success[500]} 0%,
            #f6f8f914 37%,
            #f6f8f900 50%,
            #f6f8f914 63%,
            ${colors.success[500]} 100%
        )
        0% 0% no-repeat padding-box;
    text-align: center;
`;

const ImageBorder = styled.div`
    background-color: ${colors.neutral[50]};
    border-radius: 50%;
    padding: 4px;
    width: 100%;
    height: 100%;
`;

const Image = styled.img`
    min-width: 100%;
    min-height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
    padding: 4px;
    border-radius: 50%;
    border: 1px solid ${colors.success[900]};
`;

const Flex = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const StyledSVG = styled(SVG)`
    fill: currentColor;
    path {
        fill: currentColor;
    }
`;

// const validationSchema = Yup.object().shape({
//     name: Yup.string()
//         .required("Full name is required")
//         .test(
//             "two-words",
//             "Full name must contain at least two words",
//             (value) => value && value.trim().split(/\s+/).length >= 2
//         )
//         .max(140, "Full name must be at most 140 characters"),
//     email: Yup.string()
//         .email("Invalid email")
//         .max(140, "Email must be at most 140 characters"),
//     phone: Yup.string().max(140, "Phone number must be at most 140 characters"),
//     country: Yup.object()
//         .nullable()
//         .required("Country is required"),
//     city: Yup.object().nullable(),
//     account: Yup.object()
//         .nullable()
//         .required("Account is required"),
//     title: Yup.string()
//         .required("Title is required")
//         .max(150, "Title must be at most 150 characters"),
// });

function ExtensionNewLead() {
    const { setState, getEsListe, profilListeLoading, setNewLeadObject } = useExtensionContext();
    const [search, setSearch] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(null);

    const { countryOptions, cityOptions, isLoading, updateCityOptions } = useLocationOptions();

    const handleInputChange = (newValue) => {
        setSearch(newValue);
        return newValue;
    };

    const fetchQuery = async (inputValue, page) => {
        const requestObj = {
            fn: "getAccountSuggestions",
            accountName: inputValue,
            page: page,
            pageSize: 100,
        };

        try {
            const response = await post(requestObj);
            const data = response.data;
            const options = data?.accounts?.map((item) => {
                return {
                    value: item.accountName,
                    label: item.accountName,
                };
            });

            return {
                options,
                hasMore: data.totalCount ? page * 100 < data.totalCount : false,
                additional: {
                    page: page + 1,
                },
            };
        } catch (error) {
            return {
                options: [],
                hasMore: false,
                additional: {
                    page: 1,
                },
            };
        }
    };

    const loadOptions = (inputValue, _, { page }) =>
        new Promise((resolve) => {
            const options = fetchQuery(inputValue, page);
            resolve(options);
        });

    const handleSubmit = async (values, { resetForm, setSubmitting, setErrors }) => {
        try {
            // Sequential validation
            if (!values.name || values.name.trim().split(/\s+/).length < 2) {
                toast.error("Please enter full name");
                setErrors({ name: "Full name must contain at least two words" });
                return;
            }

            if (!values.country) {
                toast.error("Please select a country");
                setErrors({ country: "Country is required" });
                return;
            }

            if (!values.account) {
                toast.error("Please add an account");
                setErrors({ account: "Account is required" });
                return;
            }

            if (!values.title) {
                toast.error("Please add a title");
                setErrors({ title: "Title is required" });
                return;
            }

            const nameAndSurname = values.name.trim().split(" ");
            const name = nameAndSurname
                .slice(0, nameAndSurname.length - 1)
                .join(" ")
                .trim();
            const surname = nameAndSurname.length > 1 ? nameAndSurname[nameAndSurname.length - 1].trim() : "";

            const result = await getEsListe(
                {
                    ad: name,
                    soyad: surname,
                    baslik: values.title,
                    sirket: values.account.value,
                    country: values.country.value,
                    city: values.city?.value,
                    tel: values.phone,
                    email: values.email,
                    kategori: "lead",
                },
                true
            );

            if (result.length === 0) {
                return;
            }

            setNewLeadObject({
                name: name,
                surname: surname,
                phone: values?.phone,
                email: values?.email,
                city: values?.city?.value,
                country: values.country.value,
                account: values.account.value,
                title: values.title,
                kategori: "lead",
            });
            setState((prevState) => ({ ...prevState, isPopitSearch: true }));
        } catch (error) {
            if (error.name === "ValidationError") {
                const formErrors = {};
                error.inner.forEach((e) => {
                    formErrors[e.path] = e.message;
                });
                setErrors(formErrors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <NewLeadContainer>
            <Heading3>Generate New Lead</Heading3>
            <Formik
                initialValues={{
                    name: "",
                    email: "",
                    phone: "",
                    country: null,
                    city: null,
                    account: null,
                    title: "",
                }}
                // validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ setFieldValue, errors, touched }) => (
                    <StyledForm>
                        <Flex>
                            <ImageSection>
                                <ImageBorder style={{ position: "relative" }}>
                                    <Image src="/media/users/default.jpg" alt="profile" />
                                </ImageBorder>
                            </ImageSection>
                            <Heading3>
                                <Field
                                    name="name"
                                    render={({ field }) => (
                                        <InputWithIcon
                                            error={errors.name && touched.name}
                                            variant={"unstyled"}
                                            placeholder={"Add Full Name"}
                                            debounceTime={0}
                                            {...field}
                                        />
                                    )}
                                />
                            </Heading3>
                        </Flex>
                        <div>
                            <ExtensionCard
                                cardTitle="Contact"
                                cardItems={[
                                    {
                                        icon: (
                                            <span style={{ color: errors.email && touched.email ? "red" : undefined }}>
                                                <FeatherIcon strokeWidth="1px" icon={"mail"} size="16" />
                                            </span>
                                        ),
                                        content: (
                                            <Field
                                                name="email"
                                                render={({ field }) => (
                                                    <InputWithIcon
                                                        variant={"unstyled"}
                                                        placeholder={"Add Email (optional)"}
                                                        debounceTime={0}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        ),
                                    },
                                ]}
                            />
                            <ExtensionCard
                                cardItems={[
                                    {
                                        icon: (
                                            <span style={{ color: errors.phone && touched.phone ? "red" : undefined }}>
                                                <FeatherIcon strokeWidth="1px" icon={"phone"} size="16" />
                                            </span>
                                        ),
                                        content: (
                                            <Field
                                                name="phone"
                                                render={({ field }) => (
                                                    <InputWithIcon
                                                        variant={"unstyled"}
                                                        placeholder={"Add Phone (optional)"}
                                                        debounceTime={0}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                        <ExtensionCard
                            cardTitle="Location"
                            cardItems={[
                                {
                                    icon: (
                                        <span style={{ color: errors.country && touched.country ? "red" : undefined }}>
                                            <StyledSVG src={toAbsoluteUrl("/media/svg/popleads/location.svg")} />
                                        </span>
                                    ),
                                    content: (
                                        <Field
                                            name="country"
                                            render={({ field: { onChange, ...field } }) => (
                                                <ExtensionInputGroup
                                                    variant="l3rnTech"
                                                    key={"country-single-select"}
                                                    placeholder={"Select Country"}
                                                    type="select"
                                                    isClearable={true}
                                                    isSearchable={true}
                                                    isCreatable={false}
                                                    isMulti={false}
                                                    options={countryOptions}
                                                    onChange={(selected) => {
                                                        setFieldValue("country", selected);
                                                        updateCityOptions(selected);
                                                        setFieldValue("city", null);
                                                    }}
                                                    disabled={isLoading}
                                                    fontSize="12.5px"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    ),
                                },
                                {
                                    icon: (
                                        <span style={{ color: errors.city && touched.city ? "red" : undefined }}>
                                            <StyledSVG src={toAbsoluteUrl("/media/svg/popleads/location.svg")} />
                                        </span>
                                    ),
                                    content: (
                                        <Field
                                            name="city"
                                            render={({ field: { onChange, ...field } }) => (
                                                <ExtensionInputGroup
                                                    variant="l3rnTech"
                                                    key={"city-single-select"}
                                                    placeholder={"Select or Add City (optional)"}
                                                    type="select"
                                                    isClearable={true}
                                                    isSearchable={true}
                                                    isCreatable={true}
                                                    formatCreateLabel={(inputValue) => `Add City: ${inputValue}`}
                                                    isMulti={false}
                                                    options={cityOptions}
                                                    onChange={(selected) => setFieldValue("city", selected)}
                                                    fontSize="12.5px"
                                                    {...field}
                                                />
                                            )}
                                        />
                                    ),
                                },
                            ]}
                        />

                        <ExtensionCard
                            cardTitle="Work"
                            cardItems={[
                                {
                                    icon: (
                                        <span style={{ color: errors.account && touched.account ? "red" : undefined }}>
                                            <StyledSVG src={toAbsoluteUrl("/media/svg/popleads/work-bag.svg")} />
                                        </span>
                                    ),
                                    content: (
                                        <div style={{ maxWidth: "295px" }}>
                                            <Field
                                                name="account"
                                                render={({ field: { onChange, ...field } }) => (
                                                    <ExtensionInputGroup
                                                        variant="l3rnTech"
                                                        key={"single-select"}
                                                        placeholder={"Add Account"}
                                                        type="select"
                                                        isClearable={true}
                                                        isSearchable={true}
                                                        isCreatable={true}
                                                        isMulti={false}
                                                        loadOptions={loadOptions}
                                                        additional={{ page: 1 }}
                                                        onChange={(selected) => {
                                                            setSelectedAccount(selected);
                                                            setFieldValue("account", selected);
                                                            if (selected === null) {
                                                                setSearch("");
                                                            }
                                                        }}
                                                        inputValue={search}
                                                        onInputChange={handleInputChange}
                                                        onMenuOpen={() => {
                                                            if (selectedAccount) {
                                                                setSearch(selectedAccount.label);
                                                                setSelectedAccount(null);
                                                            }
                                                        }}
                                                        onMenuClose={() => {
                                                            setSelectedAccount(field.value);
                                                        }}
                                                        fontSize="12.5px"
                                                        {...field}
                                                    />
                                                )}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    icon: (
                                        <span style={{ color: errors.title && touched.title ? "red" : undefined }}>
                                            <StyledSVG src={toAbsoluteUrl("/media/svg/popleads/bag-extension.svg")} />
                                        </span>
                                    ),
                                    content: (
                                        <Field
                                            name="title"
                                            render={({ field }) => (
                                                <InputWithIcon
                                                    variant={"unstyled"}
                                                    placeholder={"Add Title"}
                                                    debounceTime={0}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    ),
                                },
                            ]}
                        />
                        <ButtonWithText variant="l3rnTech" buttonStyle="default" type="submit" size="small">
                            Pop-It
                            {profilListeLoading ? (
                                <Spinner animation="border" className="ml-1" variant="light" size="sm" />
                            ) : null}
                        </ButtonWithText>
                    </StyledForm>
                )}
            </Formik>
        </NewLeadContainer>
    );
}

export default ExtensionNewLead;
